import React, { useState, useMemo } from "react";
import { Chart } from "react-google-charts";
import {
    endOfMonth, format, addMonths, parse, getYear, setMonth, getMonth, setYear
} from "date-fns";
import { SearchCrmQueryQueryVariables, useSearchCrmQueryQuery } from "../../generated/graphql";
import { SelectId } from "../../components/MaterialSelects/SelectId";
import { NonIdealState } from "../../components/NonIdealState";
import { ClientSubTypeSelect } from "../../components/MaterialSelects/ClientSubTypeSelect";
import { UserSelect } from "../../components/MaterialSelects/UserSelect";
import { ClientCompanyContactSelect } from "../../components/MaterialSelects/ClientCompanyContactSelect";
import { useUserContext } from "../../contexts/user-context";
import {
    KeyboardArrowLeft,
    KeyboardArrowRight,
    KeyboardDoubleArrowLeft,
    KeyboardDoubleArrowRight
} from "@mui/icons-material";
import WaitSpinner from "../../components/wait-spinner";
import { FormControlLabel, Switch, IconButton } from "@mui/material";
import { IsoDatePicker } from "../../components/IsoDatePicker";
import { useToast } from "../../contexts/toast";
import { SalesReportTypeSelect } from "../../components/MaterialSelects/SalesReportTypeSelect";
import { ClientCompanySelect } from "../../components/MaterialSelects/ClientCompanySelect";

// ---------- helpers ----------
const d2 = (v?: number) => (!v ? "00" : v < 10 ? `0${v}` : `${v}`);

const nowT = new Date();
const now = {
    year: getYear(nowT),
    month: getMonth(nowT),
    quarter: Math.floor(getMonth(nowT) / 3),
    half: Math.floor(getMonth(nowT) / 6)
};

const statuses = [
    { label: "Confirmed", id: "confirmed" },
    { label: "Draft", id: "draft" },
    { label: "Quote", id: "quote" },
    { label: "Confirmed and Quote", id: "confirmed_quote" },
    { label: "Confirmed, Quote and Draft", id: "confirmed_quote_draft" }
];

const status_values: Record<string, string[]> = {
    confirmed_quote_draft: ["Paid", "To invoice", "Invoiced", "Confirmed", "Part Invoiced", "Quote", "Draft"],
    confirmed_quote: ["Paid", "To invoice", "Invoiced", "Confirmed", "Part Invoiced", "Quote"],
    confirmed: ["Paid", "To invoice", "Invoiced", "Confirmed", "Part Invoiced"],
    quote: ["Quote"],
    draft: ["Draft"]
};

const quarters = [
    { label: "Q1", id: 0 },
    { label: "Q2", id: 1 },
    { label: "Q3", id: 2 },
    { label: "Q4", id: 3 }
];

const halves = [
    { label: "H1", id: 0 },
    { label: "H2", id: 1 }
];

// ---------- Month picker ----------
const MonthPicker = (props: any) => {
    const now = new Date();
    const date = parse(props.value + "-01", "yyyy-MM-dd", now);
    const month = getMonth(date);
    const year = getYear(date);
    const cy = getYear(now);

    const years = [];
    for (let i = cy + 1; i >= 2010; i--) years.push({ id: i, label: i.toString() });

    const monthLabels = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];
    const months = monthLabels.map((label, id) => ({ id, label }));

    const addMonth = (m: number) => () => {
        props.onChange({
            target: { name: props.name, value: format(addMonths(date, m), "yyyy-MM") }
        });
    };
    const changeMonth = (m: any) =>
        props.onChange({
            target: { name: props.name, value: format(setMonth(date, m.target.value), "yyyy-MM") }
        });
    const changeYear = (m: any) =>
        props.onChange({
            target: { name: props.name, value: format(setYear(date, m.target.value), "yyyy-MM") }
        });

    return (
        <div className="month-control">
            <IconButton onClick={addMonth(-12)}><KeyboardDoubleArrowLeft /></IconButton>
            <IconButton onClick={addMonth(-1)}><KeyboardArrowLeft /></IconButton>
            <SelectId labelField="label" minWidth="10rem" simpleSelect valueIsId name="month" onChange={changeMonth} options={months} value={month} label="Month" />
            <SelectId labelField="label" minWidth="10rem" simpleSelect valueIsId name="year" onChange={changeYear} options={years} value={year} label="Year" />
            <IconButton onClick={addMonth(1)}><KeyboardArrowRight /></IconButton>
            <IconButton onClick={addMonth(12)}><KeyboardDoubleArrowRight /></IconButton>
        </div>
    );
};

// ---------- Component ----------
export default function AccountManagerDashboardNB() {
    const user = useUserContext();
    const { toast } = useToast();
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

    const [formState, setFormState] = useState({
        accountManager: user.user?.id,
        clientSubType: "",
        contacts: "",
        client: "" as string | string[],
        status: "confirmed",
        periodType: "month", // month | quarter | half | year | range
        currentMonthYear: `${now.year}-${d2(now.month + 1)}`,
        currentQuarter: now.quarter.toString(),
        currentYear: now.year.toString(),
        currentHalf: now.half.toString(),
        period: `${now.year}-${now.quarter}`,
        toggleTop15Campaigns: true,
        years_comparison: false,
        completion_date: "",
        start_date: ""
    });

    const [variables, setVariables] = useState<SearchCrmQueryQueryVariables & { skip?: boolean }>(() => ({
        account_manager_ids: [user.user?.id],
        completion_date_between: {
            from: formState.currentMonthYear + "-01",
            to: format(
                endOfMonth(parse(formState.currentMonthYear + "-01", "yyyy-MM-dd", new Date())),
                "yyyy-MM-dd"
            )
        },
        status_in: status_values[formState.status]
    }));

    const { loading, error, data } = useSearchCrmQueryQuery({
        variables,
        fetchPolicy: "network-only",
        skip: !!variables?.skip
    });
    const campaigns = !loading && !error && data ? data.campaigns?.data : [];

    const filteredCampaigns = useMemo(() => {
        if (!selectedStatus) return [];
        return (campaigns || []).filter(
            (c: any) => (c.status ?? "Unknown") === selectedStatus
        );
    }, [campaigns, selectedStatus]);

    const handleFormStateChange = (name: string, value: any) => {
        const updated = { ...formState, [name]: value };

        // maintain derived 'period'
        if (updated.periodType === "month" && name === "currentMonthYear") {
            updated.period = value;
            updated.currentYear = value.split("-")[0];
        }
        if (updated.periodType === "quarter" && (name === "currentQuarter" || name === "currentYear")) {
            updated.period = `${updated.currentYear}-${updated.currentQuarter}`;
        }
        if (updated.periodType === "half" && (name === "currentHalf" || name === "currentYear")) {
            updated.period = `${updated.currentYear}-${updated.currentHalf}`;
        }
        if (updated.periodType === "year" && name === "currentYear") {
            updated.period = value;
        }

        setFormState(updated);

        // build GraphQL variables
        const v: typeof variables = {
            account_manager_ids: updated.accountManager ? [updated.accountManager] : [],
            status_in: updated.status ? status_values[updated.status] : []
        };

        if (updated.clientSubType) v.client_sub_type_ids = [updated.clientSubType];
        if (updated.contacts) v.contact_ids = [updated.contacts];
        if (updated.client && (updated.client as any[]).length > 0) v.client_ids = updated.client as any[];

        // date windows
        const curYear = parseInt(updated.currentYear, 10);
        if (updated.years_comparison) {
            delete v.completion_date_between;
            const lastTwoYears = [curYear - 2, curYear - 1, curYear];

            switch (updated.periodType) {
                case "month": {
                    const [, monthPart] = updated.currentMonthYear.split("-");
                    v.completion_date_in = lastTwoYears.map((y) => ({
                        from: `${y}-${monthPart}-01`,
                        to: format(endOfMonth(parse(`${y}-${monthPart}-01`, "yyyy-MM-dd", new Date())), "yyyy-MM-dd")
                    }));
                    break;
                }
                case "quarter": {
                    const q = parseInt(updated.currentQuarter, 10);
                    const startM = q * 3 + 1;
                    v.completion_date_in = lastTwoYears.map((y) => ({
                        from: `${y}-${d2(startM)}-01`,
                        to: format(endOfMonth(parse(`${y}-${d2(startM + 2)}-01`, "yyyy-MM-dd", new Date())), "yyyy-MM-dd")
                    }));
                    break;
                }
                case "half": {
                    const h = parseInt(updated.currentHalf, 10);
                    const firstM = d2(h * 6 + 1);
                    v.completion_date_in = lastTwoYears.map((y) => ({
                        from: `${y}-${firstM}-01`,
                        to: format(endOfMonth(parse(`${y}-${d2((h + 1) * 6)}-01`, "yyyy-MM-dd", new Date())), "yyyy-MM-dd")
                    }));
                    break;
                }
                case "year": {
                    delete v.completion_date_in;
                    const toDate = parse(`${curYear}-12-01`, "yyyy-MM-dd", new Date());
                    v.completion_date_between = { from: `${curYear - 2}-01-01`, to: format(endOfMonth(toDate), "yyyy-MM-dd") };
                    break;
                }
                case "range": {
                    if (updated.start_date && updated.completion_date && updated.client && (updated.client as any[]).length > 0) {
                        v.completion_date_between = { from: updated.start_date, to: updated.completion_date };
                    } else {
                        toast("Please select a client and both start and end dates for the range period.", { variant: "error" });
                        v.skip = true;
                    }
                    break;
                }
                default:
                    v.skip = true;
            }
        } else {
            delete v.completion_date_in;
            switch (updated.periodType) {
                case "month":
                    v.completion_date_between = {
                        from: updated.currentMonthYear + "-01",
                        to: format(endOfMonth(parse(updated.currentMonthYear + "-01", "yyyy-MM-dd", new Date())), "yyyy-MM-dd")
                    };
                    break;
                case "quarter": {
                    const q = parseInt(updated.currentQuarter, 10);
                    const y = updated.currentYear;
                    const from = parse(`${y}-${d2(q * 3 + 1)}-01`, "yyyy-MM-dd", new Date());
                    const to = parse(`${y}-${d2((q + 1) * 3)}-01`, "yyyy-MM-dd", new Date());
                    v.completion_date_between = { from: format(from, "yyyy-MM-dd"), to: format(endOfMonth(to), "yyyy-MM-dd") };
                    break;
                }
                case "half": {
                    const h = parseInt(updated.currentHalf, 10);
                    const y = updated.currentYear;
                    const from = parse(`${y}-${d2(h * 6 + 1)}-01`, "yyyy-MM-dd", new Date());
                    const to = parse(`${y}-${d2((h + 1) * 6)}-01`, "yyyy-MM-dd", new Date());
                    v.completion_date_between = { from: format(from, "yyyy-MM-dd"), to: format(endOfMonth(to), "yyyy-MM-dd") };
                    break;
                }
                case "year": {
                    const y = updated.currentYear;
                    const from = parse(`${y}-01-01`, "yyyy-MM-dd", new Date());
                    const to = parse(`${y}-12-01`, "yyyy-MM-dd", new Date());
                    v.completion_date_between = { from: format(from, "yyyy-MM-dd"), to: format(endOfMonth(to), "yyyy-MM-dd") };
                    break;
                }
                case "range": {
                    if (updated.start_date && updated.completion_date && updated.client && (updated.client as any[]).length > 0) {
                        v.completion_date_between = { from: updated.start_date, to: updated.completion_date };
                    } else {
                        toast("Please select a client and both start and end dates for the range period.", { variant: "error" });
                        v.skip = true;
                    }
                    break;
                }
                default:
                    v.skip = true;
            }
        }

        setVariables(v);
    };

    const years = useMemo(() => {
        const arr: { label: string; id: number }[] = [];
        for (let i = now.year - 5; i < now.year + 5; i++) arr.push({ label: i.toString(), id: i });
        return arr;
    }, []);

    const onChange = (evt: any) => {
        const { name, type, checked, value } = evt.target;
        handleFormStateChange(name, type === "checkbox" ? checked : value);
    };

    const renderBodyDashboard = useMemo(() => {
        if (error) return <NonIdealState title="An error has occurred" />;
        if (loading) return <WaitSpinner type="fixed" />;

        const revenueBySector: Record<string, number> = {};
        const revenueByClient: Record<string, number> = {};
        const revenueByYear: Record<string, Record<number, number>> = {};
        const revenueByST: Record<string, Record<number, number>> = {};
        const currentYear = parseInt(formState.currentYear, 10);
        const lastTwoYears = [currentYear - 2, currentYear - 1, currentYear];

        const statusOrder = [
            "Paid", "To invoice", "Invoiced", "Part Invoiced", "Confirmed", "Quote", "Draft", "Cancelled",
        ];
        const revenueByStatus: Record<string, number> = Object.fromEntries(statusOrder.map(s => [s, 0]));
        const top15CampaignsData: { name: string; revenue: number; jobref: string }[] = [];

        const updateRevenueBySalesTeamYearly = (salesTeam: any, year: number, revenue: number) => {
            if (salesTeam && salesTeam.include_dashboard_core) {
                if (!revenueByST[salesTeam.prefix]) revenueByST[salesTeam.prefix] = {};
                if (!revenueByST[salesTeam.prefix][year]) revenueByST[salesTeam.prefix][year] = 0;
                revenueByST[salesTeam.prefix][year] += revenue;
            }
        };

        if (campaigns?.length) {
            campaigns.forEach((campaign: any) => {
                const status = campaign.status ?? "Unknown";
                const revenue = (campaign.total_price_reporting) / 100;
                const clientType = campaign.clientSubType?.name || "Unknown";
                const client = campaign.clientCompany?.accounts_company || "";
                const name = campaign.name;
                const year = new Date(campaign.completion_date).getFullYear();
                const salesTeam = campaign.salesTeam || "";

                top15CampaignsData.push({ name, revenue, jobref: campaign.job_reference });

                if (revenueByStatus[status] == null) revenueByStatus[status] = 0;
                revenueByStatus[status] += revenue;

                if (clientType) {
                    if (!revenueBySector[clientType]) revenueBySector[clientType] = 0;
                    revenueBySector[clientType] += revenue;
                }

                if (client) {
                    if (!revenueByClient[client]) revenueByClient[client] = 0;
                    revenueByClient[client] += revenue;
                }

                if (lastTwoYears.includes(year)) {
                    if (!revenueByYear[client]) {
                        revenueByYear[client] = Object.fromEntries(lastTwoYears.map(y => [y, 0]));
                    }
                    revenueByYear[client][year] += revenue;
                }

                if (salesTeam) updateRevenueBySalesTeamYearly(salesTeam, year, revenue);
            });
        }

        const clientYOY: (string | number)[][] = [["Client", ...lastTwoYears.map(y => y.toString())]];
        for (const client in revenueByYear) {
            const yearData = revenueByYear[client];
            clientYOY.push([client, ...lastTwoYears.map(y => yearData[y] || 0)]);
        }

        const salesYOY: (string | number)[][] = [["Sales Team", ...lastTwoYears.map(y => y.toString())]];
        for (const team in revenueByST) {
            const yearData = revenueByST[team];
            salesYOY.push([team, ...lastTwoYears.map(y => yearData[y] || 0)]);
        }

        const top15Campaigns = top15CampaignsData.sort((a, b) => b.revenue - a.revenue).slice(0, 15);
        const topCampaignsChartData = [
            ["Campaign", "Revenue"],
            ...top15Campaigns.map(c => [c.name, c.revenue])
        ];

        // statuses: only > 0 and keep order
        const statusRows: [string, number][] = statusOrder
            .filter(s => (revenueByStatus[s] ?? 0) > 0)
            .map(s => [s, revenueByStatus[s]]);

        // append any unseen statuses with > 0 (rare)
        for (const [s, v] of Object.entries(revenueByStatus)) {
            if (v > 0 && !statusOrder.includes(s)) statusRows.push([s, v]);
        }

        const chartData = [["Status", "Revenue"], ...statusRows];

        console.log("chartDatachartData", chartData)

        const totalRevenue = statusRows.reduce((sum, [, v]) => sum + v, 0);
        if (totalRevenue === 0) {
            return <NonIdealState title="No data available for the current search. Please choose different filters to alter the chart." />;
        }

        const topFiveSectors = Object.entries(revenueBySector)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 5);

        const barChartData = [["Sector", "Revenue"], ...topFiveSectors.map(([s, v]) => [s, v])];

        const pieChartClient = [
            ["Client", "Revenue"],
            ...Object.entries(revenueByClient)
                .sort(([, a], [, b]) => b - a)
                .map(([client, revenue]) => [client, revenue])
        ];

        return (
            <div className="sales-report__controls__chartsCRM">
                {!formState.years_comparison && (
                    <Chart
                        chartType="PieChart"
                        data={chartData}
                        options={{ title: "Revenue by Status" }}
                        height="400px"
                        chartEvents={[
                            {
                                eventName: "select",
                                callback: ({ chartWrapper }) => {
                                    const chart = chartWrapper.getChart();
                                    const sel = chart.getSelection();
                                    if (!sel.length) return;
                                    const row = sel[0].row;
                                    // row maps to chartData[row+1] (row 0 is header)
                                    const statusLabel = chartData[row + 1][0] as string;
                                    setSelectedStatus(statusLabel);
                                }
                            }
                        ]}
                    />
                )}
                <div style={{ marginTop: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <h3 style={{ margin: 0 }}>
                            Campaigns – {selectedStatus} ({filteredCampaigns.length})
                        </h3>
                        <button onClick={() => setSelectedStatus(null)}>Clear</button>
                    </div>

                    {filteredCampaigns.length === 0 ? (
                        <NonIdealState title="No campaigns found for this status." />
                    ) : (
                        <div className="table-like">
                            <div className="row header">
                                <div>Job Ref</div>
                                <div>Name</div>
                                <div>Total (£)</div>
                                <div>Completion</div>
                            </div>
                            {filteredCampaigns.map((c: any) => (
                                <div key={c.id} className="row">
                                    <div>
                                        <a href={`/campaigns/${c.job_reference}`} target="_blank" rel="noreferrer">
                                            {c.job_reference}
                                        </a>
                                    </div>
                                    <div>{c.name}</div>
                                    <div>{(c.total_price_reporting ?? 0) / 100}</div>
                                    <div>{c.completion_date?.slice(0, 10)}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <Chart
                    chartType="PieChart"
                    data={pieChartClient}
                    options={{ title: "Revenue Per Client", is3D: true }}
                    height="400px"
                />

                {formState.toggleTop15Campaigns ? (
                    <div id="topCampaignsChartWrapper">
                        <Chart
                            chartType="Bar"
                            height="400px"
                            data={topCampaignsChartData}
                            options={{
                                chart: { title: "Top 15 Campaigns by Revenue", chartArea: { width: "100%" } }
                            }}
                            chartEvents={[
                                {
                                    eventName: "select",
                                    callback: ({ chartWrapper }) => {
                                        const chart = chartWrapper.getChart();
                                        const selection = chart.getSelection();
                                        if (selection.length > 0) {
                                            const idx = selection[0].row;
                                            const selectedCampaign = top15Campaigns[idx];
                                            if (selectedCampaign) {
                                                if (confirm("Do you want to open this campaign?")) {
                                                    window.open(`/campaigns/${selectedCampaign.jobref}`, "_blank");
                                                }
                                            }
                                        }
                                    }
                                }
                            ]}
                        />
                    </div>
                ) : (
                    <Chart
                        chartType="Bar"
                        height="400px"
                        data={barChartData}
                        options={{
                            chart: { title: "Top 5 Sectors", subtitle: "Client Types & Revenue" },
                            hAxis: { title: "Total Revenue", minValue: 0 },
                            vAxis: { title: "Sectors" }
                        }}
                    />
                )}

                {formState.years_comparison && (
                    !formState.client || formState.client.length === 0 ? (
                        <Chart
                            chartType="Bar"
                            height="400px"
                            data={salesYOY}
                            options={{ chart: { title: "Sales Team Performance Year over Year" }, isStacked: true }}
                        />
                    ) : (
                        <Chart
                            chartType="Bar"
                            height="400px"
                            data={clientYOY}
                            options={{ chart: { title: "Client Performance" }, isStacked: true }}
                        />
                    )
                )}
            </div>
        );
    }, [loading, error, campaigns, formState]);

    return (
        <React.Fragment>
            <div className="sales-report__controls">
                <div>
                    <UserSelect
                        valueIsId
                        simpleSelect
                        allowBlank
                        blankLabel="All Account Manager"
                        label="Account Managers"
                        value={formState.accountManager || []}
                        onChange={onChange}
                        name="accountManager"
                    />
                </div>

                <div className="sales-report__controls__type">
                    <SalesReportTypeSelect
                        valueIsId
                        simpleSelect
                        name="periodType"
                        rangePeriod={false}
                        onChange={onChange}
                        value={formState.periodType}
                        label="Period"
                    />
                </div>

                {formState.periodType === "month" && (
                    <div className="sales-report__controls__month">
                        <MonthPicker valueIsId value={formState.currentMonthYear} name="currentMonthYear" onChange={onChange} label="Month" />
                    </div>
                )}

                {formState.periodType === "quarter" && (
                    <div className="sales-report__controls__quarter month-control">
                        <SelectId name="currentQuarter" labelField="label" simpleSelect label="Quarter" onChange={onChange} options={quarters} value={formState.currentQuarter} />
                        <SelectId name="currentYear" labelField="label" label="Year" simpleSelect onChange={onChange} options={years} value={formState.currentYear} />
                    </div>
                )}

                {formState.periodType === "half" && (
                    <div className="sales-report__controls__quarter month-control">
                        <SelectId name="currentHalf" labelField="label" label="Half" simpleSelect onChange={onChange} options={halves} value={formState.currentHalf} />
                        <SelectId name="currentYear" labelField="label" label="Year" simpleSelect onChange={onChange} options={years} value={formState.currentYear} />
                    </div>
                )}

                {formState.periodType === "year" && (
                    <div className="sales-report__controls__quarter month-control">
                        <SelectId name="currentYear" label="Year" labelField="label" simpleSelect onChange={onChange} options={years} value={formState.currentYear} />
                    </div>
                )}

                {formState.periodType === "range" && (
                    <>
                        <div className="sales-report__controls__date-range">
                            <IsoDatePicker name="start_date" label="Start Date" onChange={onChange} className="grid12-2" style={{ width: "10rem" }} value={formState.start_date} />
                        </div>
                        <div>
                            <IsoDatePicker name="completion_date" label="Completion Date" onChange={onChange} className="grid12-2" style={{ width: "10rem" }} value={formState.completion_date} />
                        </div>
                    </>
                )}

                <div>
                    <ClientSubTypeSelect
                        valueIsId
                        simpleSelect
                        allowBlank
                        blankLabel="All Client Type"
                        label="Client Types"
                        value={formState.clientSubType || []}
                        onChange={onChange}
                        name="clientSubType"
                    />
                </div>

                <div>
                    <ClientCompanyContactSelect
                        valueIsId
                        allowBlank
                        blankLabel="All Contacts"
                        label="Contact"
                        value={formState.contacts || []}
                        onChange={onChange}
                        name="contacts"
                    />
                </div>

                <div style={{ width: "10rem" }}>
                    <ClientCompanySelect
                        valueIsId
                        multiple
                        allowBlank
                        blankLabel="All Clients"
                        label="Client"
                        value={formState.client || []}
                        onChange={onChange}
                        name="client"
                    />
                </div>

                <div>
                    <SelectId
                        labelField="label"
                        label="Status"
                        name="status"
                        simpleSelect
                        allowBlank
                        value={formState.status || []}
                        onChange={onChange}
                        options={statuses}
                        style={{ flexBasis: "15rem" }}
                    />
                </div>

                <div>
                    <FormControlLabel
                        disabled={!!formState.years_comparison}
                        control={<Switch name="toggleTop15Campaigns" color="primary" />}
                        label="Top 15 Campaigns"
                        checked={formState.toggleTop15Campaigns}
                        onChange={onChange}
                    />
                </div>
            </div>

            {renderBodyDashboard}
        </React.Fragment>
    );
}
